import {FormControl, FormField} from 'platform/components';
import {Grid, Show} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {indexBy, isNil} from 'ramda';
import {isString} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {useTenant, useVatRatesOptions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useDebouncedCallback} from 'shared/web';

import {roundToTwoDecimals} from '../../RentalOrderHeader/utils/roundToTwoDecimals';
import {RentVehicleFormValues} from '../types/RentVehicleFormValues';

interface CustomPriceRowProps extends RequiredTestIdProps {
  control: FormControl<RentVehicleFormValues>;
  formApi: UseFormReturn<RentVehicleFormValues>;
}

export function CustomPriceRow(props: CustomPriceRowProps) {
  const [vatType, isCustomPriceEnabled] = useWatch({
    control: props.control,
    name: [`customPrice.vatType`, 'isCustomPriceEnabled'],
  });

  const [lastTouchedField, setLastTouchedField] = useState<'withVat' | 'withoutVat'>('withoutVat');

  const [vatRatesOptions] = useVatRatesOptions();
  const vatRatesByCode = indexBy((vatRate) => vatRate.value, vatRatesOptions);
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const {tenantCurrency} = useTenant();

  const handleRecalculatePriceWithVat = useDebouncedCallback(
    (priceWithoutVat: number | null, changedVatRate?: string) => {
      setLastTouchedField('withoutVat');
      const absoluteVatRate = vatRatesByCode?.[changedVatRate ?? vatType ?? '']?.rate;

      if (isNil(priceWithoutVat) || isNil(absoluteVatRate)) {
        return;
      }

      const rate = parseFloat(absoluteVatRate);
      const newPriceWithVat = roundToTwoDecimals(priceWithoutVat * (1 + rate / 100));
      const newVatPrice = roundToTwoDecimals(newPriceWithVat - priceWithoutVat);
      props.formApi.setValue(`customPrice.withVat`, newPriceWithVat);
      props.formApi.setValue(`customPrice.vat`, newVatPrice);
    },
    200
  );

  const handleRecalculatePriceWithoutVat = useDebouncedCallback(
    (priceWithVat: number | null, changedVatRate?: string) => {
      setLastTouchedField('withVat');
      const absoluteVatRate = vatRatesByCode?.[changedVatRate ?? vatType ?? '']?.rate;

      if (isNil(priceWithVat) || isNil(absoluteVatRate)) {
        return;
      }

      const rate = parseFloat(absoluteVatRate);
      const newPriceWithoutVat = roundToTwoDecimals(priceWithVat / (1 + rate / 100));
      const newVatPrice = roundToTwoDecimals(priceWithVat - newPriceWithoutVat);
      props.formApi.setValue(`customPrice.withoutVat`, newPriceWithoutVat);
      props.formApi.setValue(`customPrice.vat`, newVatPrice);
    },
    200
  );

  const handleRecalculateAfterVatRateChange = (vatRate: string | number | null) => {
    if (!isString(vatRate)) {
      return;
    }

    const values = props.formApi.getValues('customPrice');

    if (lastTouchedField === 'withoutVat' && values?.withoutVat) {
      handleRecalculatePriceWithVat(values.withoutVat, vatRate);
    }
    if (lastTouchedField === 'withVat' && values?.withVat) {
      handleRecalculatePriceWithoutVat(values.withVat, vatRate);
    }
  };

  return (
    <Show when={isCustomPriceEnabled}>
      <Grid columns={4}>
        <FormField
          control={props.control}
          type="currency"
          isRequired
          name="customPrice.withoutVat"
          label={i18n.t('page.rentalOrder.labels.customPriceWithoutVat')}
          currency={tenantCurrency}
          decimalPlaces={2}
          onChange={(value) => handleRecalculatePriceWithVat(value)}
          data-testid={suffixTestId('customPriceWithoutVat', props)}
        />

        <FormField
          control={props.control}
          type="currency"
          isRequired
          name="customPrice.withVat"
          label={i18n.t('page.rentalOrder.labels.customPriceWithVat')}
          currency={tenantCurrency}
          decimalPlaces={2}
          onChange={(value) => handleRecalculatePriceWithoutVat(value)}
          data-testid={suffixTestId('customPriceWithVat', props)}
        />

        <FormField
          control={props.control}
          type="currency"
          name="customPrice.vat"
          decimalPlaces={2}
          label={`${i18n.t('entity.vehicle.labels.vat')} ${formatCurrencySymbol(tenantCurrency)}`}
          currency={tenantCurrency}
          isDisabled
          data-testid={suffixTestId('customPriceVat', props)}
        />

        <FormField
          control={props.control}
          type="choice"
          name="customPrice.vatType"
          label={i18n.t('entity.vehicle.labels.vat')}
          options={vatRatesOptions}
          isNotClearable
          onChange={handleRecalculateAfterVatRateChange}
          data-testid={suffixTestId('customPriceVatRate', props)}
        />
      </Grid>
    </Show>
  );
}
